import { AgendaGrid } from "@components/AgendaGrid";
import { BackgroundPaths } from "@components/BackgroundPaths";
import { CombinedLogo } from "@components/CombinedLogo";
import { DemoLauncher } from "@components/DemoLauncher";
import { useCustomer } from "@contexts/CustomerContext";
import { useCustomerData } from "@contexts/CustomerContext";
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@repo/ui/core";
import {
  IconArchive,
  IconCalendar,
  IconMessageQuestion,
  IconPresentation,
} from "@repo/ui/icons";
import classes from "@styles/onsite.module.css";
import { Link } from "react-router";

export function LandingPage() {
  const customer = useCustomer();
  const { agenda, agendaSections } = useCustomerData();
  const basePath = `/${customer.id}/onsite`;

  return (
    <>
      <Box
        pos="relative"
        py={120}
        className={classes.heroGradient}
        style={{ overflow: "hidden" }}
      >
        <BackgroundPaths
          colorFrom={customer.theme.pathColorFrom}
          colorTo={customer.theme.pathColorTo}
        />
        <Container size="xl" pos="relative" style={{ zIndex: 1 }}>
          <Stack align="center" gap="xl">
            <Badge
              size="lg"
              variant="light"
              leftSection={<IconCalendar size={14} />}
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--customer-accent) 12%, transparent)",
                color: "var(--customer-accent-light)",
              }}
            >
              {customer.onsiteDates}
            </Badge>
            <CombinedLogo size="xl" />
            <Group mt="xl">
              <Button
                component={Link}
                to={`${basePath}/slides`}
                size="sm"
                leftSection={<IconPresentation size={20} />}
                style={{ backgroundColor: "var(--customer-accent)" }}
              >
                Slides
              </Button>
              <Button
                component={Link}
                to={`${basePath}/parking`}
                size="sm"
                variant="outline"
                leftSection={<IconArchive size={20} />}
                style={{
                  borderColor:
                    "color-mix(in srgb, var(--customer-accent) 40%, transparent)",
                  color: "var(--customer-accent-light)",
                }}
              >
                Parking Lot
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>

      <Box className={classes.sectionLine} />

      <Container size="xl" py="xl">
        <Stack gap={60}>
          <Box>
            <Group gap="sm" mb="xl" align="baseline">
              <Title order={2}>Session Agenda</Title>
              <Text size="sm" c="dimmed">
                {agendaSections.length} sections
              </Text>
            </Group>
            <AgendaGrid sections={agendaSections} />
          </Box>

          <Grid gutter="lg">
            {agenda.map((day, dayIdx) => (
              <Grid.Col key={day.label} span={{ base: 12, md: 6 }}>
                <Card
                  padding="xl"
                  radius="md"
                  h="100%"
                  className={classes.cardSurface}
                  style={{
                    borderLeft:
                      dayIdx === 0
                        ? "3px solid var(--mantine-color-blue-5)"
                        : "3px solid var(--mantine-color-violet-5)",
                  }}
                >
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Title order={3}>{day.label}</Title>
                      <Badge
                        variant="light"
                        color={dayIdx === 0 ? "blue" : "violet"}
                      >
                        {day.date}
                      </Badge>
                    </Group>
                    {day.sessions.map((session) => (
                      <Box
                        key={session.title}
                        p="sm"
                        style={{
                          borderLeft: "2px solid var(--customer-accent)",
                          background: "rgba(255,255,255,0.015)",
                          borderRadius:
                            "0 var(--mantine-radius-sm) var(--mantine-radius-sm) 0",
                        }}
                      >
                        <Group justify="space-between" mb={4}>
                          <Text size="sm" fw={600}>
                            {session.title}
                          </Text>
                          <Text
                            size="xs"
                            style={{ color: "var(--mantine-color-cyan-4)" }}
                          >
                            {session.time}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          <Text
                            component="span"
                            size="xs"
                            fw={500}
                            style={{ color: "var(--mantine-color-gray-5)" }}
                          >
                            {session.audience}
                          </Text>
                          {" — "}
                          {session.description}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>

          <DemoLauncher />

          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card
                component={Link}
                to={`${basePath}/parking`}
                padding="xl"
                radius="md"
                h="100%"
                className={classes.cardElevated}
                style={{
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                <Group gap="md">
                  <ThemeIcon size={48} radius="xl" variant="light" color="cyan">
                    <IconArchive size={24} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={600} size="lg">
                      Parking Lot
                    </Text>
                    <Text size="sm" c="dimmed">
                      Track questions, action items, and follow-ups
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card
                component={Link}
                to={`${basePath}/faq`}
                padding="xl"
                radius="md"
                h="100%"
                className={classes.cardElevated}
                style={{
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                <Group gap="md">
                  <ThemeIcon
                    size={48}
                    radius="xl"
                    variant="light"
                    color="violet"
                  >
                    <IconMessageQuestion size={24} />
                  </ThemeIcon>
                  <Box>
                    <Text fw={600} size="lg">
                      FAQ
                    </Text>
                    <Text size="sm" c="dimmed">
                      Searchable answers to common questions
                    </Text>
                  </Box>
                </Group>
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </>
  );
}
